import React, {useEffect, useState} from 'react';
import {inject, observer} from 'mobx-react';
import {tapyblAiHomeText} from './tapyblAiHomeText';
import './styles.css';
import {useNavigate, useParams} from 'react-router-dom';
import {Icon} from '../../../uiToolkit/Icon/Icon';
import {Tour} from '../../../modules/Tour';
import TapyblAIHomeStore, {HomeGroupsGlance} from './store/TapyblAIHomeStore';
import {getGroupReels} from '../../../data/models/Microreels/MicroreelGroup';
import {LearnerListItem} from '../../../data/models/Learners/Learner';
import {DashboardData} from '../../../data/models/Workspace/Dashboard';
import {truncateNumberTo2Decimals} from '../../../utils/getTimeTextFromSeconds';
import NavigationStore from '../../../navigation/store/NavigationStore';
import {APP_PERMISSIONS, isCreatorRole} from '../../../data/permissions';
import {studioPaths} from '../../../modules/MicroreelStudio/paths';

interface Props {
  username?: string;
  tapyblAIHomeStore?: TapyblAIHomeStore;
  navigationStore?: NavigationStore;
}

const ExpandIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden>
    <path
      d="M6 1H2.5A1.5 1.5 0 0 0 1 2.5V6M10 1h3.5A1.5 1.5 0 0 1 15 2.5V6M6 15H2.5A1.5 1.5 0 0 1 1 13.5V10M10 15h3.5a1.5 1.5 0 0 0 1.5-1.5V10"
      stroke="currentColor"
      strokeWidth="1.4"
      strokeLinecap="round"
    />
  </svg>
);

// "Your content pipeline" is hidden for now; flip to bring it back.
const SHOW_PIPELINE = false;

// Shimmer placeholder shown inside a glance card while its data loads.
const GlanceSkeleton = ({statCount}: {statCount: number}) => (
  <>
    <div className="HomeGlanceStats">
      {Array.from({length: statCount}, (_, index) => (
        <div className="HomeGlanceStat" key={index}>
          <span className="HomeSkeleton HomeSkeletonValue" />
          <span className="HomeSkeleton HomeSkeletonLabel" />
        </div>
      ))}
    </div>
    <span className="HomeSkeleton HomeSkeletonBar" />
    <div className="HomeGlanceList">
      {Array.from({length: 3}, (_, index) => (
        <span className="HomeSkeleton HomeSkeletonRow" key={index} />
      ))}
    </div>
  </>
);

const TapyblAIHome = inject(
  'tapyblAIHomeStore',
  'navigationStore',
)(
  observer(({tapyblAIHomeStore, navigationStore}: Props) => {
    // undefined = still loading (each widget shows its own preloader);
    // null/empty = loaded without data.
    const [dashboard, setDashboard] = useState<DashboardData | null>();
    const [groupsGlance, setGroupsGlance] = useState<HomeGroupsGlance>();
    const [learners, setLearners] = useState<LearnerListItem[]>();
    const [documentsCount, setDocumentsCount] = useState(0);
    const {organizationId} = useParams();
    const navigate = useNavigate();
    // "Learning at a glance" is not shown to creators.
    const showGlance = !isCreatorRole(navigationStore!.userPermissions);

    useEffect(() => {
      if (!organizationId) return;
      if (SHOW_PIPELINE || showGlance) {
        tapyblAIHomeStore!
          .getDashboard(organizationId)
          .then(data => setDashboard(data ?? null));
        tapyblAIHomeStore!
          .getGlanceGroups(organizationId)
          .then(setGroupsGlance);
      }
      if (showGlance) {
        tapyblAIHomeStore!.getGlanceLearners(organizationId).then(setLearners);
      }
      if (SHOW_PIPELINE) {
        tapyblAIHomeStore!
          .getDocumentsCount(organizationId)
          .then(setDocumentsCount);
      }
    }, [organizationId, showGlance]);

    const coursesGlanceLoading =
      dashboard === undefined || groupsGlance === undefined;
    const learnersGlanceLoading =
      dashboard === undefined || learners === undefined;
    const completed = dashboard?.completedCourses || 0;
    const inProgress = dashboard?.inProgressCourses || 0;
    const notStarted = dashboard?.notStartedCourses || 0;
    const statusTotal = completed + inProgress + notStarted;
    const publishedCourses = Math.max(
      (dashboard?.amountOfCourses || 0) - (dashboard?.draftCoursesNumbers || 0),
      0,
    );
    const avgScore = dashboard?.avgCoursesScore || 0;

    const segmentWidth = (value: number) =>
      statusTotal ? `${(value / statusTotal) * 100}%` : '0%';

    return (
      <div className="TapyblAiHomeContainer">
        <div
          className={`TapyblAiHomeScroll ${
            !SHOW_PIPELINE && !showGlance ? 'TapyblAiHomeScrollCentered' : ''
          }`}>
          <Tour section="aiHome" />

          <div className="HomeTopRow">
            <div className="HomeHeroCard microLessonsCard">
              <div className="HomeHeroIllustration">
                <Icon name="microreel-hero.svg" className="HomeHeroIcon" />
              </div>
              <div className="HomeHeroContent">
                <h1 className="HomeHeroTitle">{tapyblAiHomeText.heroTitle}</h1>
                <p className="HomeHeroSubtitle">
                  {tapyblAiHomeText.heroSubtitle}
                </p>
                <div className="HomeActions">
                  {navigationStore!.userPermissions.includes(
                    APP_PERMISSIONS.ORG_EDIT_COURSES,
                  ) && (
                    <>
                      <button
                        className="HomeButtonLime"
                        onClick={() => navigate('generate_microreel')}>
                        <span className="HomeButtonPlus">+</span>
                        {tapyblAiHomeText.newMicroreel}
                      </button>
                      <button
                        className="HomeButtonOutline"
                        onClick={() =>
                          navigate(studioPaths(organizationId).create)
                        }>
                        <span className="HomeButtonIcon" aria-hidden="true">
                          <Icon name="microreel.svg" />
                        </span>
                        {tapyblAiHomeText.studio}
                        <span className="HomeButtonBadge">
                          {tapyblAiHomeText.beta}
                        </span>
                      </button>
                    </>
                  )}
                  <button
                    className="HomeButtonOutline"
                    onClick={() => navigate('create_post')}>
                    <span className="HomeButtonPlus">+</span>
                    {tapyblAiHomeText.newPost}
                  </button>
                  <button
                    className="HomeButtonOutline"
                    onClick={() => navigate('../feedback_questions/create')}>
                    <span className="HomeButtonPlus">+</span>
                    {tapyblAiHomeText.newFeedback}
                  </button>
                </div>
              </div>
            </div>
            <div className="HomeCourseCard">
              <div className="HomeCourseContent">
                <h2 className="HomeCourseTitle">
                  {tapyblAiHomeText.groupReelsTitle}
                </h2>
                <p className="HomeCourseSubtitle">
                  {tapyblAiHomeText.groupReelsSubtitle}
                </p>
                <div className="HomeActions">
                  <button
                    className="HomeButtonDark"
                    onClick={() => navigate('../courses/create')}>
                    {tapyblAiHomeText.newCourse}
                  </button>
                  <button
                    className="HomeButtonOutline"
                    onClick={() => navigate('../courses')}>
                    {tapyblAiHomeText.browseCourses}
                  </button>
                </div>
              </div>
              <div className="HomeCourseIllustration">
                <Icon name="course-hero.svg" className="HomeCourseIcon" />
              </div>
            </div>
          </div>

          {SHOW_PIPELINE && (
            <>
              <h2 className="HomeSectionTitle">
                {tapyblAiHomeText.pipelineTitle}
              </h2>
              <div className="HomePipeline">
                <div
                  className="HomePipelineCard"
                  onClick={() => navigate('../library')}>
                  <div className="HomePipelineTop">
                    <Icon
                      name="document-card.svg"
                      className="HomePipelineIcon"
                    />
                    <button
                      className="HomePipelineAdd"
                      onClick={e => {
                        e.stopPropagation();
                        navigate('../library');
                      }}>
                      +
                    </button>
                  </div>
                  <h3 className="HomePipelineTitle">
                    {tapyblAiHomeText.documents}
                  </h3>
                  <span className="HomePipelineStat">
                    {documentsCount} {tapyblAiHomeText.uploaded}
                  </span>
                </div>
                <span className="HomePipelineArrow">→</span>
                <div
                  className="HomePipelineCard"
                  onClick={() => navigate('../microreels')}>
                  <div className="HomePipelineTop">
                    <Icon
                      name="microreel-card.svg"
                      className="HomePipelineIcon"
                    />
                    <button
                      className="HomePipelineAdd"
                      onClick={e => {
                        e.stopPropagation();
                        navigate('generate_microreel');
                      }}>
                      +
                    </button>
                  </div>
                  <h3 className="HomePipelineTitle">
                    {tapyblAiHomeText.microreels}
                  </h3>
                  <span className="HomePipelineStat">
                    {groupsGlance?.microreelsCount ?? 0}{' '}
                    {tapyblAiHomeText.generated}
                  </span>
                </div>
                <span className="HomePipelineArrow">→</span>
                <div
                  className="HomePipelineCard"
                  onClick={() => navigate('../courses')}>
                  <div className="HomePipelineTop">
                    <Icon name="course-card.svg" className="HomePipelineIcon" />
                    <button
                      className="HomePipelineAdd"
                      onClick={e => {
                        e.stopPropagation();
                        navigate('../courses/create');
                      }}>
                      +
                    </button>
                  </div>
                  <h3 className="HomePipelineTitle">
                    {tapyblAiHomeText.courses}
                  </h3>
                  <span className="HomePipelineStat">
                    {publishedCourses} {tapyblAiHomeText.published}
                  </span>
                </div>
              </div>
            </>
          )}

          {showGlance && (
            <>
              <h2 className="HomeSectionTitle">
                {tapyblAiHomeText.glanceTitle}
              </h2>
              <div className="HomeGlanceRow">
                <div className="HomeGlanceCard">
                  <button
                    className="HomeGlanceExpand"
                    onClick={() => navigate('../analytics')}>
                    <ExpandIcon />
                  </button>
                  {coursesGlanceLoading ? (
                    <GlanceSkeleton statCount={4} />
                  ) : (
                    <>
                      <div className="HomeGlanceStats">
                        <div className="HomeGlanceStat">
                          <span className="HomeGlanceValue">
                            {dashboard?.courseNumbers || 0}
                          </span>
                          <span className="HomeGlanceLabel">
                            {tapyblAiHomeText.courses}
                          </span>
                        </div>
                        <div className="HomeGlanceStat">
                          <span className="HomeGlanceValue">{completed}</span>
                          <span className="HomeGlanceLabel">
                            <i className="HomeDot HomeDotLime" />
                            {tapyblAiHomeText.completed}
                          </span>
                        </div>
                        <div className="HomeGlanceStat">
                          <span className="HomeGlanceValue">{inProgress}</span>
                          <span className="HomeGlanceLabel">
                            <i className="HomeDot HomeDotBlue" />
                            {tapyblAiHomeText.inProgress}
                          </span>
                        </div>
                        <div className="HomeGlanceStat">
                          <span className="HomeGlanceValue">{notStarted}</span>
                          <span className="HomeGlanceLabel">
                            <i className="HomeDot HomeDotOrange" />
                            {tapyblAiHomeText.notStarted}
                          </span>
                        </div>
                      </div>
                      <div className="HomeGlanceBar">
                        <div
                          className="HomeBarSegment HomeBarLime"
                          style={{width: segmentWidth(completed)}}
                        />
                        <div
                          className="HomeBarSegment HomeBarBlue"
                          style={{width: segmentWidth(inProgress)}}
                        />
                        <div
                          className="HomeBarSegment HomeBarOrange"
                          style={{width: segmentWidth(notStarted)}}
                        />
                      </div>
                      <div className="HomeGlanceList">
                        {(groupsGlance?.groups ?? []).map(group => {
                          const reelsCount = getGroupReels(group).length;
                          const completions = group.completedNumber || 0;
                          return (
                            <div
                              key={group.id}
                              className="HomeGlanceListRow HomeGlanceListRowLink"
                              onClick={() =>
                                navigate(`../microreels/${group.id}`)
                              }>
                              <span className="HomeGlanceListName">
                                <i
                                  className={`HomeDot ${
                                    completions > 0
                                      ? 'HomeDotLime'
                                      : 'HomeDotOrange'
                                  }`}
                                />
                                {group.title}
                              </span>
                              <span className="HomeGlanceListMeta">
                                {tapyblAiHomeText.reelsCount(reelsCount)}
                              </span>
                            </div>
                          );
                        })}
                        {(groupsGlance?.groups?.length ?? 0) > 0 && (
                          <button
                            className="HomeViewMore"
                            onClick={() => navigate('../microreels')}>
                            {tapyblAiHomeText.viewMore}
                          </button>
                        )}
                      </div>
                    </>
                  )}
                </div>
                <div className="HomeGlanceCard">
                  <button
                    className="HomeGlanceExpand"
                    onClick={() => navigate('../analytics')}>
                    <ExpandIcon />
                  </button>
                  {learnersGlanceLoading ? (
                    <GlanceSkeleton statCount={2} />
                  ) : (
                    <>
                      <div className="HomeGlanceStats">
                        <div className="HomeGlanceStat">
                          <span className="HomeGlanceValue">
                            {truncateNumberTo2Decimals(avgScore)}%
                          </span>
                          <span className="HomeGlanceLabel">
                            <i className="HomeDot HomeDotTeal" />
                            {tapyblAiHomeText.avgScore}
                          </span>
                        </div>
                        <div className="HomeGlanceStat">
                          <span className="HomeGlanceValue">
                            {dashboard?.learnerNumbers || 0}
                          </span>
                          <span className="HomeGlanceLabel">
                            {tapyblAiHomeText.learnersTotal}
                          </span>
                        </div>
                      </div>
                      <div className="HomeGlanceBar HomeGlanceBarTrack">
                        <div
                          className="HomeBarSegment HomeBarTeal"
                          style={{width: `${Math.min(avgScore, 100)}%`}}>
                          {avgScore > 10 && (
                            <span className="HomeBarLabel">
                              {truncateNumberTo2Decimals(avgScore)}%
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="HomeGlanceList">
                        {(learners ?? []).map(learner => {
                          const initials = learner.name
                            .split(' ')
                            .filter(Boolean)
                            .map(word => word[0])
                            .slice(0, 2)
                            .join('')
                            .toUpperCase();
                          return (
                            <div
                              key={learner.learnerId}
                              className="HomeGlanceListRow">
                              <span className="HomeGlanceListName">
                                <span className="HomeLearnerAvatar">
                                  {initials}
                                </span>
                                {learner.name}
                              </span>
                            </div>
                          );
                        })}
                        {(learners?.length ?? 0) > 0 && (
                          <button
                            className="HomeViewMore"
                            onClick={() => navigate('../learners')}>
                            {tapyblAiHomeText.viewMore}
                          </button>
                        )}
                        {avgScore === 0 && (
                          <p className="HomeGlanceNote">
                            {tapyblAiHomeText.noScoresNote}
                          </p>
                        )}
                      </div>
                    </>
                  )}
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    );
  }),
);

export default TapyblAIHome;
